/**
 * Retired public market slugs (DEV-10186).
 *
 * The `de` COUNTRY market is retired: Germany is served by the `eu` market, and
 * German becomes a language variant of it. Keeping the old URLs alive (indexed
 * pages, ad links, printed inserts, other people's backlinks) is four
 * permanent redirect entries in `apps/store/next.config.ts` `redirects()`,
 * which Next resolves in the routing layer before the proxy runs. Nothing here
 * performs a redirect.
 *
 * What this module owns is the two questions the rest of the app asks about a
 * retired market:
 *
 * - Can a shopper still CHOOSE it? No. `isRetiredMarket` filters it out of the
 *   region selector, the geo modal, the sitemap and the hreflang set.
 * - Whose language preference does its successor inherit? A `/de-en` shopper's
 *   `lang_pref={"de":"en"}` has to keep meaning "English" once they land on
 *   `/eu`. A `next.config.ts` redirect cannot write a cookie, so the preference
 *   is inherited at READ time: `retiredMarketsReplacedBy`, called by gate 3 of
 *   `resolve-language-redirect.ts`.
 *
 * Retirement means "cannot be picked", not "never existed": a two-year-old
 * German order still has to resolve its market's currency and formatting, so
 * `marketsData` keeps the row and this filters the menus.
 */

import { parseMarketSlug } from "./languages"

/** Retired slug → the live slug that replaced it. Whole segments only. */
const RETIRED_MARKETS: Readonly<Record<string, string>> = {
  "de": "eu",
  "de-en": "eu",
}

/**
 * Is this slug a market that no longer exists?
 *
 * Case-insensitive and whitespace-tolerant: market slugs are lowercase in URLs,
 * but a hand-typed or mis-cased link must still be recognized. `hasOwnProperty`
 * rather than a truthiness check so an inherited key like `constructor` cannot
 * report itself as a retired market.
 */
export const isRetiredMarket = (
  marketSegment: string | null | undefined
): boolean =>
  Object.prototype.hasOwnProperty.call(
    RETIRED_MARKETS,
    marketSegment?.trim().toLowerCase() ?? ""
  )

/**
 * The retired BASE markets whose traffic now lands on `market`.
 *
 * Exists so a language preference expressed under a retired market survives its
 * retirement. `lang_pref` is keyed by base market, so a shopper who explicitly
 * chose English on the German market carries `{"de":"en"}`, and after `/de-en`
 * redirects to the bare `/eu`, nothing under the `eu` key says "English". The
 * language resolver would read that as an unresolved market, guess from
 * `Accept-Language`, and send a German browser to `/eu-de`: the shopper asked
 * for English and got German.
 */
export const retiredMarketsReplacedBy = (market: string): string[] => {
  const normalized = market.trim().toLowerCase()
  const predecessors = new Set<string>()
  for (const [from, to] of Object.entries(RETIRED_MARKETS)) {
    if (parseMarketSlug(to).market !== normalized) continue
    predecessors.add(parseMarketSlug(from).market)
  }
  return [...predecessors]
}
